import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from '@zillow/zrs-comet';
import { Button } from '@zillow/constellation';
import { updateApplicantRedirectPath } from '@zg-rentals/screening-js/lib/flux/actions/ScreeningActions';
import { getNeedsToIdVerifySelector } from '../../../../../../selectors';
import { getApplicantPath, getIdVerificationPath, getLandlordReviewPath, URLS } from '../../../../../../constants/urls';
import { IcraaComplianceContext } from '../../../../../../contexts/IcraaComplianceContext';
import { getShowNavLinkAsButton } from '../../../../../../utils/applicantDetailsUtil';
import { getPageLocationForOverviewSection } from '../../../../../../utils/route';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import { updateIdVerifying } from '../../../../../../actions/landlordActionCreators';
import { useGetIsIdvModuleEnabled } from '../../../../../../hooks/useGetSplitAb';

export default function SummaryNavLinkButton({
  applicant,
  sectionId,
  title,
  label = 'View',
  isCustomLoading,
  customShowNavLinkAsButton,
  onClick,
}) {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const location = useLocation();
  const { userId: applicantId, zgAuthZone } = applicant;
  const { propertyId, applicationId } = useQueryVariables();
  const { isLoading: isLegalComplianceLoading, icraaAcceptedForApplicant } = useContext(IcraaComplianceContext);
  const needsToIdVerify = useSelector(getNeedsToIdVerifySelector());
  const isIdvModuleEnabled = useGetIsIdvModuleEnabled();
  const isIcraaRequired = sectionId !== 'applicant';

  const pageLocation = getPageLocationForOverviewSection(sectionId);
  const pageLink = getApplicantPath({
    propertyId,
    applicationId,
    pageLocation,
    zgAuthZone,
    applicantId,
  });
  const landlordReviewLink = getLandlordReviewPath(propertyId, applicationId);

  const link = needsToIdVerify
    ? isIdvModuleEnabled
      ? getIdVerificationPath(propertyId)
      : getApplicantPath({
          propertyId,
          applicationId,
          pageLocation: `${URLS.SCREENING}/${URLS.SCREENING_LANDLORD_JUNCTION}`,
          zgAuthZone,
          applicantId,
        })
    : pageLink;

  const showNavLinkAsButton = getShowNavLinkAsButton({
    icraaAcceptedForApplicant,
    isIcraaRequired,
    needsToIdVerify,
    customShowNavLinkAsButton,
  });
  const isLoading = isLegalComplianceLoading || isCustomLoading;

  function onButtonClick() {
    if (needsToIdVerify) {
      // Where to redirect upon successful ID verification
      if (isIdvModuleEnabled) {
        dispatch(
          updateIdVerifying({ completedLink: landlordReviewLink, previousLink: `${match.url}${location.search}` }),
        );
      } else {
        dispatch(updateApplicantRedirectPath(pageLink));
      }
    }
    onClick();
  }

  return (
    <Button
      {...(!showNavLinkAsButton &&
        !isLoading && {
          as: Link,
          to: link,
        })}
      disabled={isLoading}
      onClick={onButtonClick}
      className={cx('ScreeningSectionNavLink__Link', `zon_ScreeningSectionNavLink_${sectionId}`)}
      id={`view-${sectionId}-button`}
      buttonType="secondary"
      aria-label={`View ${title}`}
    >
      {isLoading ? <Spinner /> : label}
    </Button>
  );
}

SummaryNavLinkButton.propTypes = {
  applicant: PropTypes.shape({
    userId: PropTypes.string.isRequired,
    zgAuthZone: PropTypes.string.isRequired,
  }),
  sectionId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string,
  isCustomLoading: PropTypes.bool,
  customShowNavLinkAsButton: PropTypes.bool,
  onClick: PropTypes.func,
};

SummaryNavLinkButton.defaultProps = {
  onClick() {},
};

SummaryNavLinkButton.displayName = 'SummaryNavLinkButton';
